'use client';

import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const clamp = (value: number, min: number, max: number) => {
  return Math.max(min, Math.min(value, max));
};

const BracketWord = ({ word }: { word: string }) => {
  const ref = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(1440);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 0.9', 'center center'],
  });

  useEffect(() => {
    const updateViewportWidth = () => setViewportWidth(window.innerWidth);
    updateViewportWidth();
    window.addEventListener('resize', updateViewportWidth);
    return () => window.removeEventListener('resize', updateViewportWidth);
  }, []);

  const bracketTravel = clamp(viewportWidth * 0.18, 42, 220);
  const xOffset = useTransform(scrollYProgress, [0, 1], [0, bracketTravel]);

  return (
    <div
      ref={ref}
      className="my-7 flex items-center justify-center whitespace-nowrap text-4xl font-medium tracking-tight text-[#1d1d1d] md:my-16 md:text-[7vw] sm:text-6xl"
    >
      <motion.span style={{ x: useTransform(xOffset, (v) => -v) }} className="inline-block font-normal">
        [
      </motion.span>
      <span className="mx-2 sm:mx-4 md:mx-6">{word}</span>
      <motion.span style={{ x: xOffset }} className="inline-block font-normal">
        ]
      </motion.span>
    </div>
  );
};

const RevealImage = ({ src, alt }: { src: string; alt: string }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 0.9', 'center center'],
  });

  const clipPath = useTransform(
    scrollYProgress,
    [0, 1],
    ['inset(35% 35% 35% 35%)', 'inset(0% 0% 0% 0%)'],
  );

  return (
    <div
      ref={ref}
      className="relative mx-auto my-16 h-[58vh] w-full max-w-[90vw] px-4 md:my-32 md:h-[72vh] lg:h-[90vh]"
    >
      <motion.div style={{ clipPath }} className="relative h-full w-full overflow-hidden rounded-sm">
        <img src={src} alt={alt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
        <div className="pointer-events-none absolute inset-0 bg-[#1d1d1d]/30" />
      </motion.div>
    </div>
  );
};

export const PremiumTextSection = () => {
  return (
    <section className="relative z-10 flex w-full flex-col items-center bg-white py-24 md:py-48">
      <div className="mb-10 w-full max-w-[90vw] text-center md:mb-16 md:max-w-5xl md:px-10 lg:px-0">
        <h2 className="text-3xl font-black uppercase tracking-tight sm:text-5xl md:text-6xl">
          Découvrez notre salon
        </h2>
        <p className="mx-auto mt-5 max-w-3xl text-sm leading-relaxed text-gray-600 sm:text-base md:text-lg">
          Un espace pensé pour les hommes exigeants: ambiance soignée, technique maîtrisée et service haut de gamme pour chaque rendez-vous.
        </p>
      </div>
      <BracketWord word="Ambiance" />
      <RevealImage
        src="/images/salon1.webp"
        alt="Salle principale du salon Eleganze avec deux fauteuils et une grande baie vitrée"
      />
      <BracketWord word="Détails" />
      <RevealImage
        src="/images/salon4.webp"
        alt="Poste Eleganze avec miroir rond, tondeuses et produits prêts pour le service"
      />
      <BracketWord word="Gestes" />
      <RevealImage
        src="/images/salon5.webp"
        alt="Travail aux ciseaux et au peigne sur une coupe réalisée au salon Eleganze"
      />
      <BracketWord word="Outils" />
      <RevealImage
        src="/images/salon7.webp"
        alt="Sélection d'outils de barbier disposés avec soin sur un plateau noir"
      />
      <BracketWord word="Intemporelle" />
    </section>
  );
};
